import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';

const Terms = () => {
  const sections = [
    {
      title: 'Service Agreement',
      content: `By engaging Jet'n'Seal for cleaning services, you agree to these terms and conditions. Our services include patio cleaning, pressure washing, driveway cleaning, and related outdoor surface cleaning services.`
    },
    {
      title: 'Booking and Scheduling',
      content: `All bookings are subject to availability and weather conditions. We reserve the right to reschedule services due to adverse weather conditions that may affect safety or service quality. Customers will be notified as soon as possible of any schedule changes.`
    },
    {
      title: 'Pricing and Payment',
      content: `Prices are quoted based on the scope of work assessed. Payment is due upon completion of services unless otherwise arranged. We accept cash, bank transfer, and card payments. Additional charges may apply for services beyond the original scope.`
    },
    {
      title: 'Customer Responsibilities',
      content: `Customers must ensure clear access to all areas requiring cleaning. Any obstacles, fragile items, or plants should be moved or protected prior to our arrival. Please inform us of any specific concerns or delicate surfaces beforehand.`
    },
    {
      title: 'Health and Safety',
      content: `We maintain comprehensive public liability insurance and follow strict health and safety protocols. Customers and pets should remain clear of work areas during cleaning operations for safety reasons.`
    },
    {
      title: 'Satisfaction Guarantee',
      content: `We stand behind our work with a satisfaction guarantee. If you're not completely satisfied with our service, please contact us within 48 hours of completion, and we'll work to resolve any issues.`
    },
    {
      title: 'Limitations and Liability',
      content: `While we take utmost care, some staining may be permanent due to age, surface condition, or stain type. We cannot guarantee removal of all stains or restoration to original condition. Our liability is limited to the value of services provided.`
    },
    {
      title: 'Cancellation Policy',
      content: `Cancellations must be made at least 24 hours in advance. Late cancellations may incur a charge. We reserve the right to cancel services due to unsafe working conditions or customer non-compliance with agreed terms.`
    },
    {
      title: 'Environmental Commitment',
      content: `We use environmentally responsible cleaning products and methods wherever possible. All waste water and debris are disposed of in accordance with local environmental regulations.`
    },
    {
      title: 'Data Protection',
      content: `Customer information is collected and processed in accordance with UK data protection laws. Your personal information is used solely for service provision and will not be shared with third parties without consent.`
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-900 to-blue-700 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h1 className="text-4xl lg:text-6xl font-bold mb-6">Terms & Conditions</h1>
            <p className="text-xl text-blue-100 max-w-3xl mx-auto">
              Please read these terms and conditions carefully before using our services.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Terms Content */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="bg-white rounded-2xl shadow-lg p-8 lg:p-12"
          >
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                Jet'n'Seal Terms of Service
              </h2>
              <p className="text-gray-600">
                Last updated: {new Date().toLocaleDateString('en-GB')}
              </p>
            </div>

            <div className="space-y-8">
              {sections.map((section, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="border-l-4 border-blue-600 pl-6"
                >
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">
                    {index + 1}. {section.title}
                  </h3>
                  <p className="text-gray-700 leading-relaxed">
                    {section.content}
                  </p>
                </motion.div>
              ))}
            </div>

            <div className="mt-12 pt-8 border-t border-gray-200">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Contact Information</h3>
              <div className="space-y-2 text-gray-700">
                <p><strong>Business Name:</strong> Jet'n'Seal</p>
                <p><strong>Service Areas:</strong> Malton, Norton, Pickering & Surrounding Areas</p>
                <p className="mt-4">
                  <Link to="/contact-us" className="text-blue-600 hover:text-blue-700 font-medium">
                    Contact us for enquiries →
                  </Link>
                </p>
              </div>
            </div>

            <div className="mt-8 p-4 bg-blue-50 rounded-lg">
              <p className="text-sm text-blue-800">
                <strong>Note:</strong> These terms and conditions are subject to change. 
                Customers will be notified of any significant changes. If you have any questions 
                about these terms, please contact us before booking our services.
              </p>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Terms;